/* © NXAPP Limited */
/**
 * A compact "what happened" strip for the top of a module. Each metric is one
 * card: the big number is TODAY, with This week (7d) and This month (30d)
 * underneath and a 14-day sparkline so the daily trend is visible at a glance.
 *
 * Pass a `series` per metric — per-day counts, oldest→today, length 30.
 */
import { sumSeries } from '@/lib/trend'

export type Tone = 'teal' | 'indigo' | 'amber' | 'green' | 'rose' | 'gray'
export interface TrendMetric { label: string; tone: Tone; series: number[] }

const TONE: Record<Tone, { text: string; bar: string }> = {
  teal:   { text: 'text-teal-700',   bar: 'fill-teal-500' },
  indigo: { text: 'text-indigo-700', bar: 'fill-indigo-500' },
  amber:  { text: 'text-amber-700',  bar: 'fill-amber-500' },
  green:  { text: 'text-green-700',  bar: 'fill-green-500' },
  rose:   { text: 'text-rose-700',   bar: 'fill-rose-500' },
  gray:   { text: 'text-gray-700',   bar: 'fill-gray-400' },
}

function Sparkline({ data, tone }: { data: number[]; tone: Tone }) {
  const last = data.slice(-14)
  const max = Math.max(1, ...last)
  const w = 4, gap = 2, h = 26
  const width = last.length * (w + gap)
  return (
    <svg viewBox={`0 0 ${width} ${h}`} width={width} height={h} className="mt-2 overflow-visible" aria-hidden="true">
      {last.map((v, i) => {
        const bh = Math.max(1, Math.round((v / max) * (h - 2)))
        return <rect key={i} x={i * (w + gap)} y={h - bh} width={w} height={bh} rx={1} className={v > 0 ? TONE[tone].bar : 'fill-gray-200'} />
      })}
    </svg>
  )
}

function Card({ m }: { m: TrendMetric }) {
  const today = m.series.at(-1) ?? 0
  const week = sumSeries(m.series.slice(-7))
  const month = sumSeries(m.series)
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{m.label}</p>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-2xl font-semibold tabular-nums ${TONE[m.tone].text}`}>{today}</span>
        <span className="text-[10px] text-gray-400">today</span>
      </div>
      <p className="text-[11px] text-gray-500">
        This week <span className="font-semibold text-gray-700 tabular-nums">{week}</span>
        <span className="mx-1 text-gray-300">·</span>
        This month <span className="font-semibold text-gray-700 tabular-nums">{month}</span>
      </p>
      <Sparkline data={m.series} tone={m.tone} />
    </div>
  )
}

export default function TrendStrip({ metrics, loading }: { metrics?: TrendMetric[]; loading?: boolean }) {
  if (loading && !metrics) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        {[0, 1, 2].map(i => <div key={i} className="rounded-lg border border-gray-200 bg-white h-[104px] animate-pulse" />)}
      </div>
    )
  }
  if (!metrics?.length) return null
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
      {metrics.map(m => <Card key={m.label} m={m} />)}
    </div>
  )
}
